import React, {
  FC,
  PropsWithChildren,
  useState,
  useEffect,
  ComponentType,
  ReactNode,
} from 'react'
import { asyncWithLDProvider, basicLogger } from 'launchdarkly-react-client-sdk'

const LaunchDarklyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [LDProvider, setLDProvider] = useState<ComponentType<{
    children: ReactNode
  }> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const LAUNCH_DARKLY_CLIENT_SIDE_KEY =
    process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_KEY

  useEffect(() => {
    const initializeLaunchDarkly = async () => {
      if (!LAUNCH_DARKLY_CLIENT_SIDE_KEY) {
        console.warn('LAUNCH_DARKLY_CLIENT_SIDE_KEY is not set')
        setIsLoading(false)
        return
      }

      try {
        const logging = () => {
          return process.env.REACT_APP_ENV === 'development'
            ? basicLogger({ level: 'debug' })
            : basicLogger({ level: 'error' })
        }

        const provider = await asyncWithLDProvider({
          clientSideID: LAUNCH_DARKLY_CLIENT_SIDE_KEY,
          options: {
            logger: logging(),
            sendEvents: true,
            streaming: true,
          },
          deferInitialization: true,
        })

        setLDProvider(() => provider)
      } catch (error) {
        console.error('Failed to initialize LaunchDarkly:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeLaunchDarkly()
  }, [LAUNCH_DARKLY_CLIENT_SIDE_KEY])

  // Show children while LaunchDarkly is initializing
  if (isLoading || !LDProvider) {
    return <>{children}</>
  }

  return <LDProvider>{children}</LDProvider>
}

export default LaunchDarklyProvider
